import {
  DEFAULT_API_HOST_PATTERNS,
  DEFAULT_DENY_HOST_PATTERNS,
  isApiUrl,
  type XviewPerfTimingRegistry,
} from "./firstApiTiming";

export interface FirstApiRequestHookOptions {
  apiHostPatterns?: readonly RegExp[];
  denyHostPatterns?: readonly RegExp[];
  onFirstApi?: (info: {
    startTime: number;
    url: string;
    detectionMethod: "request_hook";
  }) => void;
}

export function installFirstApiRequestHook(
  options: FirstApiRequestHookOptions = {},
): void {
  if (typeof window === "undefined") return;

  // Native WebViews can evaluate the head IIFE while preloaded/hidden, then
  // issue the first app request after becoming visible. Install the hook early
  // regardless of initial visibility so that later request is still observed.
  const ns = (window.__xviewPerfTiming__ ??= {} as XviewPerfTimingRegistry);

  if (ns.firstApiRequestStartTime != null) return;

  // Idempotency guard for hot-reload / multiple installs before any fetch fires
  const win = window as Window & {
    __xviewPerfFetchHookInstalled__?: boolean;
  };
  if (win.__xviewPerfFetchHookInstalled__) return;
  win.__xviewPerfFetchHookInstalled__ = true;

  const apiHostPatterns = options.apiHostPatterns ?? DEFAULT_API_HOST_PATTERNS;
  const denyHostPatterns =
    options.denyHostPatterns ?? DEFAULT_DENY_HOST_PATTERNS;
  const selfHost = window.location?.host ?? "";

  const tryRecord = (url: string): void => {
    if (ns.firstApiRequestStartTime != null) return;
    try {
      if (!isApiUrl(url, apiHostPatterns, denyHostPatterns, selfHost)) return;
      const startTime = performance.now();
      ns.firstApiRequestStartTime = startTime;
      ns.firstApiRequestUrl = url;
      ns.firstApiRequestDetectionMethod = "request_hook";
      // firstApiRequestResponseEnd is intentionally left undefined — PO back-fills it when response arrives.
      try {
        options.onFirstApi?.({
          startTime,
          url,
          detectionMethod: "request_hook",
        });
      } catch {
        /* user callback errors are isolated */
      }
    } catch {
      /* defensive: never let instrumentation errors affect the caller */
    }
  };

  // --- fetch wrapper ---
  if (typeof window.fetch === "function") {
    const nativeFetch = window.fetch;
    try {
      window.fetch = function patchedFetch(
        input: RequestInfo | URL,
        init?: RequestInit,
      ): Promise<Response> {
        let url: string | null = null;
        try {
          if (typeof input === "string") url = input;
          else if (input instanceof URL) url = input.toString();
          else if (input instanceof Request) url = input.url;
        } catch {
          /* never let URL extraction break the user's fetch */
        }
        if (url !== null) tryRecord(url);
        return nativeFetch.call(this, input, init);
      };
    } catch {
      /* fetch may be a getter-only property in some bridge contexts; fall through to XHR-only */
    }
  }

  // --- XHR wrapper ---
  if (typeof XMLHttpRequest === "function") {
    const proto = XMLHttpRequest.prototype;
    const nativeOpen = proto.open;
    try {
      proto.open = function patchedOpen(
        this: XMLHttpRequest,
        _method: string,
        url: string | URL,
        ..._rest: unknown[]
      ): void {
        try {
          const urlStr = typeof url === "string" ? url : url.toString();
          tryRecord(urlStr);
        } catch {
          /* never break the user's XHR */
        }
        // eslint-disable-next-line prefer-rest-params
        nativeOpen.apply(
          this,
          arguments as unknown as Parameters<typeof nativeOpen>,
        );
      };
    } catch {
      /* prototype may be frozen by a userscript */
    }
  }
}
